import { useState, type FormEvent } from 'react';

import { Alert } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import { Field } from '@/shared/ui/field';
import { Input } from '@/shared/ui/input';

import { formatDate } from '../billing-format';

import type { LearnerProfile } from '@mio/shared';

export function ProfileForm({
  profile,
  saving,
  error,
  saved,
  onSave,
}: {
  profile: LearnerProfile;
  saving: boolean;
  error: string | null;
  saved: boolean;
  onSave: (displayName: string) => void;
}) {
  const [name, setName] = useState(profile.displayName ?? '');
  const trimmed = name.trim();
  const invalid = trimmed.length === 0 || trimmed.length > 80;
  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!invalid) onSave(trimmed);
  }
  return (
    <form onSubmit={submit} className="space-y-5" aria-labelledby="profile-title">
      <Field
        label="Name"
        htmlFor="displayName"
        error={name.length > 0 && invalid ? 'Use 1 to 80 characters.' : undefined}
        hint="Mio and your Workbench colleagues address you by this name."
      >
        <Input
          id="displayName"
          name="displayName"
          autoComplete="name"
          value={name}
          invalid={name.length > 0 && invalid}
          onChange={(event) => setName(event.target.value)}
        />
      </Field>
      <dl className="grid gap-4 sm:grid-cols-2">
        <div className="account-field-static">
          <dt>Email</dt>
          <dd>{profile.email}</dd>
        </div>
        <div className="account-field-static">
          <dt>Member since</dt>
          <dd>{formatDate(profile.createdAt)}</dd>
        </div>
      </dl>
      {error ? <Alert>{error}</Alert> : null}
      <div className="flex items-center gap-3">
        <Button
          type="submit"
          disabled={invalid || trimmed === (profile.displayName ?? '')}
          loading={saving}
        >
          Save changes
        </Button>
        {saved ? (
          <span role="status" className="text-fg-muted text-sm">
            Saved.
          </span>
        ) : null}
      </div>
    </form>
  );
}
